import React, { useEffect, useRef } from "react";
import "./App.scss";
import { BrowserRouter } from "react-router-dom";
import ApplicationFooter from "./components/ApplicationFooter";
import ApplicationHeader from "./components/ApplicationHeader";
import ProfileMenu from "./components/UI/ProfileMenu";
import { useSelector, useDispatch } from "react-redux";
import { IAppState } from "./store/app-state";
import BurgerMenu from "./components/UI/BurgerMenu";
import ResponseMessage from "./components/UI/ResponseMessage";
import { hideAllMenus } from "store/application/application-action";
import { fetchExperiments } from "store/experiments/actions";
import RoutesComponents from "./components/RoutesComponents";
import GoCtrl from "./components/ads/goCtrl";
import { QueryClient, QueryClientProvider } from "react-query";
import { PreferencesPopup } from "components/UI/PreferencesPopup";
import { CookieNoticeWrapper } from "components/widgets";
import AuthPopup from "./components/UI/Popups/AuthPopup/AuthPopup";
import { className } from "utils/strings";
import { useDeviceType } from "hooks";
import TagManager from "react-gtm-module";
import ScrollToTop from "components/UI/ScrollToTop";
import { ChangedEmailPopup } from "./components/UI/Popups/ChangedEmailPopup/ChangedEmailPopup";
import { MigrateImgurPopup } from "components/UI/MigrateImgurPopup";
import { useHeaderScroll } from "hooks/useHeaderScroll";
import { setAppHeight } from "./utils/hacks";
import { metrics } from "utils/metrics";

const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GTM_ID ?? ""
};

TagManager.initialize(tagManagerArgs);

const isSpecialPage = (): boolean => {
  const path = window.location.pathname;

  if (path.indexOf("/ifr/") === 0) {
    return true;
  }

  return false;
};

window.addEventListener("resize", setAppHeight);
setAppHeight();

metrics.init();

const App: React.FC = () => {
  const isProfileMenuOpen = useSelector((state: IAppState) => state.application.isProfileMenuOpen);
  const isBurgerMenuOpen = useSelector((state: IAppState) => state.application.isBurgerMenuOpen);
  const isSearchBarOpen = useSelector((state: IAppState) => state.application.isSearchBarOpen);
  const deviceType = useDeviceType();
  const { show: isOpenAuthModal } = useSelector((state: IAppState) => state.application.authModal);
  const dispatch = useDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { isFixed } = useHeaderScroll({ wrapperRef });

  useEffect(() => {
    const body = document.body;
    if (isProfileMenuOpen || isBurgerMenuOpen || isSearchBarOpen) {
      // eslint-disable-next-line no-magic-numbers
      if (window.innerWidth > 1170 && isProfileMenuOpen) {
        return;
      }
      body.style.overflow = "hidden";
    } else {
      body.style.overflow = "auto";
    }
  }, [isProfileMenuOpen, isBurgerMenuOpen, isSearchBarOpen]);

  // Pre-load experiments to speed-up avaiability to components.
  useEffect(() => {
    dispatch(fetchExperiments());
  });

  const handleBindedKey = (event: React.KeyboardEvent) => {
    if (event.code === "Escape") {
      dispatch(hideAllMenus());
    }
  };

  useEffect(() => {
    if (document.head.querySelectorAll("title").length === 0) {
      document.title = "";
    }
  }, []);

  useEffect(() => {
    const handleMouseUp = (e: MouseEvent) => {
      if (e.button === 3) {
        window.history.back();
      }
    };

    document.addEventListener("mouseup", handleMouseUp);

    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, []);

  const queryClient = new QueryClient();

  const showNavigation = !isSpecialPage();

  return (
    <QueryClientProvider client={queryClient}>
      <div onKeyDown={handleBindedKey} className={className({
        "App": true,
        "nav": showNavigation,
        "nonav": !showNavigation,
        "scrolled": isFixed,
        [deviceType]: true,
      })}>
        <BrowserRouter>
          <ScrollToTop>
            <GoCtrl />

            {showNavigation && <ApplicationHeader />}

            {isBurgerMenuOpen && <BurgerMenu isBurgerMenuOpen={isBurgerMenuOpen} />}

            {isProfileMenuOpen && <ProfileMenu isProfileMenuOpen={isProfileMenuOpen} />}

            <ResponseMessage />

            {isOpenAuthModal && <AuthPopup />}

            <div className="Wrapper" id="scrollableDiv" ref={wrapperRef}>
              <div className="routeWrapper">
                <RoutesComponents />
              </div>
            </div>

            {showNavigation && (
              <div className="navWrapper">
                <ApplicationFooter />
              </div>
            )}
          </ScrollToTop>
          <ChangedEmailPopup />
          {!isSpecialPage() && (
            <>
              <PreferencesPopup />
              <MigrateImgurPopup />
              <CookieNoticeWrapper />
            </>
          )}
        </BrowserRouter>
      </div>
    </QueryClientProvider>
  );
};

export default App;
